import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  SparklesIcon,
  Trash2Icon,
  Loader2Icon,
  CheckCircle2Icon,
  DownloadIcon,
  DatabaseIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
} from 'lucide-react';
import axiosInstance from '../services/axiosInstance';
import { toast } from 'sonner';

// Örnek Veriler (Solo) — kullanıcı isteğe bağlı olarak Solo dashboard'unu
// örnek verilerle doldurabilir veya temizleyebilir. Toggle davranışında:
//   - Kapalıyken: "Örnek Verileri Yükle" butonu gösterilir
//   - Açıkken:   sayıyı ve "Örnek Verileri Temizle" butonu gösterilir
// Yalnızca `seeded: true` işaretli kayıtlar etkilenir — gerçek veri asla dokunulmaz.
const SoloDemoDataSettings = () => {
  const [status, setStatus] = useState({ has_demo_data: false, count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/solo/demo-data/status');
      setStatus(r.data || { has_demo_data: false, count: 0 });
    } catch (_e) {
      /* sessizce yut — status opsiyonel */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleClear = async () => {
    const confirmed = window.confirm(
      `${status.count} örnek harcama, işletmeleri ve projeleri kalıcı olarak silinecek. Bu işlem geri alınamaz. Onaylıyor musunuz?`
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post('/solo/demo-data/clear');
      const d = r.data?.deleted || {};
      toast.success(
        `Temizlendi: ${d.expenses || 0} harcama, ${d.suppliers || 0} işletme, ${d.projects || 0} proje silindi.`
      );
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Temizleme başarısız');
    } finally {
      setBusy(false);
    }
  };

  const handleLoad = async () => {
    const confirmed = window.confirm(
      'Dashboard&apos;unuza örnek harcama, işletme ve proje verileri eklenecek. Uygulamayı denemek için idealdir. Devam edelim mi?'
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post('/solo/demo-data/load');
      if (r.data?.already_loaded) {
        toast.info(`Örnek veriler zaten yüklü (${r.data.count} kayıt)`);
      } else {
        toast.success(`${r.data?.count || '?'} örnek harcama yüklendi 🎉`);
      }
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Örnek veri yüklenemedi');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">
        <Loader2Icon className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }

  const isOn = status.has_demo_data;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="solo-demo-data-settings">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Örnek Veriler (Mockup)</h2>
        <p className="text-sm text-gray-500 mt-1">
          Uygulamayı test etmek için örnek harcama, işletme ve proje verileriyle
          dashboard&apos;unuzu doldurabilirsiniz. Gerçek veriye geçmek istediğinizde tek tıkla
          temizlenir. <strong>Sadece işaretli örnek kayıtlar etkilenir</strong> — kendi girdileriniz asla dokunulmaz.
        </p>
      </div>

      {/* Toggle bandı — durum + tek tıkla load/clear */}
      <Card
        className={`p-5 border-2 transition-colors ${
          isOn ? 'border-teal-300 bg-teal-50' : 'border-gray-200 bg-gray-50'
        }`}
        data-testid={isOn ? 'solo-demo-data-on' : 'solo-demo-data-off'}
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-11 h-11 rounded-lg border flex items-center justify-center flex-shrink-0 ${
              isOn ? 'bg-white border-teal-200' : 'bg-white border-gray-200'
            }`}
          >
            {isOn ? (
              <SparklesIcon className="w-5 h-5 text-teal-600" />
            ) : (
              <DatabaseIcon className="w-5 h-5 text-gray-500" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`text-lg font-semibold ${isOn ? 'text-teal-900' : 'text-gray-900'}`}>
                Örnek veri modu
              </h3>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  isOn ? 'bg-teal-600 text-white' : 'bg-gray-300 text-gray-800'
                }`}
                data-testid="solo-demo-status-pill"
              >
                {isOn ? (
                  <>
                    <ToggleRightIcon className="w-3 h-3" /> AÇIK
                  </>
                ) : (
                  <>
                    <ToggleLeftIcon className="w-3 h-3" /> KAPALI
                  </>
                )}
              </span>
            </div>

            <p className={`text-sm mt-1 ${isOn ? 'text-teal-800' : 'text-gray-600'}`}>
              {isOn ? (
                <>
                  Hesabınızda <strong>{status.count}</strong> örnek harcama, ~18 işletme ve 3 proje bulunuyor.
                  Solo planın tüm özelliklerini (dashboard, işletmeler, YoY grafik, PDF/CSV raporu)
                  gerçekmiş gibi deneyebilirsiniz.
                </>
              ) : (
                <>
                  Dashboard&apos;unuz boş — sadece kendi girdilerinizle çalışıyorsunuz. Uygulamayı
                  denemek isterseniz aşağıdan örnek verileri yükleyebilirsiniz.
                </>
              )}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {isOn ? (
                <Button
                  onClick={handleClear}
                  disabled={busy}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid="solo-demo-clear-btn"
                >
                  {busy ? (
                    <>
                      <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                      Temizleniyor...
                    </>
                  ) : (
                    <>
                      <Trash2Icon className="w-4 h-4 mr-2" />
                      Örnek Verileri Temizle
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleLoad}
                  disabled={busy}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="solo-demo-load-btn"
                >
                  {busy ? (
                    <>
                      <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                      Yükleniyor...
                    </>
                  ) : (
                    <>
                      <DownloadIcon className="w-4 h-4 mr-2" />
                      Örnek Verileri Yükle
                    </>
                  )}
                </Button>
              )}

              {isOn && (
                <span className="text-xs text-teal-700 inline-flex items-center gap-1">
                  <CheckCircle2Icon className="w-3.5 h-3.5" />
                  Yalnızca örnek kayıtlar silinir — kendi eklediğiniz veriler korunur.
                </span>
              )}
              {!isOn && (
                <span className="text-xs text-gray-500">
                  Yüklenecek: ~156 harcama · 18 işletme · 3 proje
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="text-xs text-gray-400 leading-relaxed">
        <strong>Not:</strong> Örnek veriler son 14 aya yayılmış şekilde eklenir, YoY (yıllık
        trend) grafiklerini gerçekçi göstermek için. &quot;Temizle&quot; işlemi geri
        alınamaz; ancak istediğiniz zaman tekrar yükleyebilirsiniz.
      </div>
    </div>
  );
};

export default SoloDemoDataSettings;
